import {
  computed,
  inject,
  type InjectionKey,
  provide,
  ref,
  type Ref,
  toValue,
  type MaybeRefOrGetter,
  watch,
} from "vue";
import {
  useSQDCWorkflowDefinitionsQuery,
  useSQDCWorkflowInstancesQuery,
  type SQDCWorkflowDefinitionsQueryVariables,
  type SQDCWorkflowInstancesQueryVariables,
} from "@/graphql";
import { toSQDCWorkflowDefinition, toSQDCWorkflowInstance } from "./repository";
import type { SQDCWorkflowInstance } from "./domain";
import type { SQDCWorkflowDefinition } from "./domain";

type Variables<T> = T | Ref<T> | (() => T);

const SQDCWorkflowInstancesBatchInjectionKey: InjectionKey<Ref<SQDCWorkflowInstance[]>> = Symbol(
  "SQDCWorkflowInstancesBatch",
);
const SQDCWorkflowDefinitionsBatchInjectionKey: InjectionKey<{
  boardId: Ref<number | undefined>;
  definitions: Ref<SQDCWorkflowDefinition[]>;
  loading: Ref<boolean>;
}> = Symbol("SQDCWorkflowDefinitionsBatch");
const SQDCWorkflowInstancesRefetchInjectionKey: InjectionKey<() => Promise<unknown>> = Symbol(
  "SQDCWorkflowInstancesRefetch",
);
const SQDCWorkflowInstancesLoadingInjectionKey: InjectionKey<Ref<boolean>> = Symbol(
  "SQDCWorkflowInstancesLoading",
);

export function useSQDCWorkflowDefinitions(
  variables: Variables<SQDCWorkflowDefinitionsQueryVariables> = {},
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  const query = useSQDCWorkflowDefinitionsQuery(variables, () => ({
    enabled: toValue(enabled),
    // Definitions are immutable versions. Start menus must still check the server for the
    // current, non-superseded version instead of reusing a cached predecessor indefinitely.
    fetchPolicy: "cache-and-network",
  }));
  const definitions = computed(
    () => query.result.value?.SQDCWorkflowDefinitions.map(toSQDCWorkflowDefinition) ?? [],
  );
  return { ...query, definitions };
}

export function provideSQDCWorkflowDefinitionsForBoard(
  boardId: MaybeRefOrGetter<number | undefined>,
) {
  const resolvedBoardId = computed(() => toValue(boardId));
  const { definitions, loading } = useSQDCWorkflowDefinitions(
    computed(() => ({
      boardIds: resolvedBoardId.value == null ? undefined : [resolvedBoardId.value],
    })),
    computed(() => resolvedBoardId.value != null),
  );
  provide(SQDCWorkflowDefinitionsBatchInjectionKey, {
    boardId: resolvedBoardId,
    definitions,
    loading,
  });
}

export function useSQDCWorkflowDefinitionsForBoard(
  variables: Variables<SQDCWorkflowDefinitionsQueryVariables>,
) {
  const provided = inject(SQDCWorkflowDefinitionsBatchInjectionKey, undefined);
  const usesProvided = computed(() => {
    const boardIds = toValue(variables).boardIds;
    return (
      provided != null &&
      (boardIds == null ||
        (Array.isArray(boardIds)
          ? boardIds.length === 1 && boardIds[0] === provided.boardId.value
          : boardIds === provided.boardId.value))
    );
  });
  const query = useSQDCWorkflowDefinitions(
    variables,
    computed(() => !usesProvided.value),
  );
  return {
    ...query,
    definitions: computed(() =>
      usesProvided.value ? provided!.definitions.value : query.definitions.value,
    ),
    loading: computed(() => (usesProvided.value ? provided!.loading.value : query.loading.value)),
  };
}

export function useSQDCWorkflowDefinitionForId(
  id: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  const provided = inject(SQDCWorkflowDefinitionsBatchInjectionKey, undefined);
  const definitionId = computed(() => toValue(id));
  const providedDefinition = computed(() =>
    provided?.definitions.value.find((definition) => definition.id === definitionId.value),
  );
  const query = useSQDCWorkflowDefinitions(
    computed(() => ({ ids: definitionId.value == null ? [] : [definitionId.value] })),
    computed(
      () =>
        toValue(enabled) &&
        definitionId.value != null &&
        provided?.loading.value !== true &&
        providedDefinition.value == null,
    ),
  );
  return {
    definition: computed(
      () =>
        providedDefinition.value ??
        query.definitions.value.find((definition) => definition.id === definitionId.value),
    ),
    loading: computed(
      () =>
        toValue(enabled) &&
        definitionId.value != null &&
        (provided?.loading.value === true ||
          (providedDefinition.value == null && query.loading.value)),
    ),
  };
}

export function useSQDCWorkflowInstances(
  variables: Variables<SQDCWorkflowInstancesQueryVariables> = {},
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  const query = useSQDCWorkflowInstancesQuery(variables, () => ({
    enabled: toValue(enabled),
    fetchPolicy: "cache-and-network",
  }));
  const instances = computed(
    () => query.result.value?.SQDCWorkflowInstances.map(toSQDCWorkflowInstance) ?? [],
  );
  return { ...query, instances };
}

export function provideSQDCWorkflowInstancesBatch(
  eventIds: MaybeRefOrGetter<number[]>,
  actionIds: MaybeRefOrGetter<number[]>,
) {
  const variables = computed(() => ({
    eventIds: toValue(eventIds),
    actionIds: toValue(actionIds),
  }));
  const query = useSQDCWorkflowInstancesQuery(variables, () => ({
    enabled: variables.value.eventIds.length > 0 || variables.value.actionIds.length > 0,
    fetchPolicy: "cache-and-network",
  }));
  const lastResolvedInstances = ref<SQDCWorkflowInstance[]>([]);
  watch(
    [query.result, query.loading],
    ([result, loading]) => {
      if (!loading && result != null) {
        lastResolvedInstances.value = result.SQDCWorkflowInstances.map(toSQDCWorkflowInstance);
      }
    },
    { immediate: true },
  );
  const instances = computed(() =>
    query.loading.value
      ? lastResolvedInstances.value
      : (query.result.value?.SQDCWorkflowInstances.map(toSQDCWorkflowInstance) ??
        lastResolvedInstances.value),
  );
  const hasResolved = ref(false);
  watch(
    [query.loading, query.result, query.error],
    ([loading, result, error]) => {
      if (!loading && (result != null || error != null)) hasResolved.value = true;
    },
    { immediate: true },
  );
  const initialReady = computed(
    () =>
      variables.value.eventIds.length + variables.value.actionIds.length === 0 || hasResolved.value,
  );
  provide(SQDCWorkflowInstancesBatchInjectionKey, instances);
  provide(SQDCWorkflowInstancesRefetchInjectionKey, () => Promise.resolve(query.refetch()));
  provide(SQDCWorkflowInstancesLoadingInjectionKey, query.loading);
  return { ...query, instances, initialReady };
}

export function useSQDCWorkflowInstanceForId(id: MaybeRefOrGetter<string | undefined>) {
  const provided = inject(SQDCWorkflowInstancesBatchInjectionKey, undefined);
  const refetchProvided = inject(SQDCWorkflowInstancesRefetchInjectionKey, undefined);
  const providedLoading = inject(SQDCWorkflowInstancesLoadingInjectionKey, undefined);
  const instanceId = computed(() => toValue(id));
  const providedInstance = computed(() =>
    provided?.value.find((instance) => instance.workflowInstanceId === instanceId.value),
  );
  const query = useSQDCWorkflowInstances(
    computed(() => ({ ids: instanceId.value == null ? [] : [instanceId.value] })),
    computed(
      () =>
        instanceId.value != null &&
        providedInstance.value == null &&
        providedLoading?.value !== true,
    ),
  );
  return {
    instance: computed(
      () =>
        providedInstance.value ??
        query.instances.value.find((instance) => instance.workflowInstanceId === instanceId.value),
    ),
    loading: computed(
      () => instanceId.value != null && (providedLoading?.value === true || query.loading.value),
    ),
    refetch: () =>
      instanceId.value == null
        ? Promise.resolve()
        : providedInstance.value != null
          ? refetchProvided?.()
          : query.refetch(),
  };
}

export function useSQDCWorkflowInstancesForItem(
  variables: Variables<SQDCWorkflowInstancesQueryVariables>,
) {
  const batchedInstances = inject(SQDCWorkflowInstancesBatchInjectionKey, undefined);
  const query = useSQDCWorkflowInstancesQuery(variables, {
    enabled: batchedInstances == null,
    fetchPolicy: "cache-and-network",
  });
  const resolvedVariables = computed(() => toValue(variables));
  const instances = computed(() => {
    if (batchedInstances == null) {
      return query.result.value?.SQDCWorkflowInstances.map(toSQDCWorkflowInstance) ?? [];
    }
    const { eventId, actionId } = resolvedVariables.value;
    return batchedInstances.value.filter(
      (instance) =>
        (eventId != null && instance.eventIds.includes(eventId)) ||
        (actionId != null && instance.actionIds.includes(actionId)),
    );
  });
  return { ...query, instances };
}
